import { DataGridPro } from '@mui/x-data-grid-pro';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { DebounceInput } from 'react-debounce-input';
import { Box, IconButton, InputAdornment, TextField, Typography } from '@mui/material';

export const UserAdminToolbar = (tableName, enableSearch = true, actionButton) => {
  const GenerateSearchInput = forwardRef((props, inputRef) => {
    const endSearch = () => {
      props.hideSearch();
      props.clearSearch();
    };

    return (
      <TextField
        className="search-box"
        size="small"
        inputRef={inputRef}
        id="qa-list-search-input"
        onChange={props.onChange}
        value={props.value}
        variant="outlined"
        onKeyDown={({ code }) => (code === 'Escape' ? endSearch() : null)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon className="search-adornment-icon" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={endSearch}>
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    );
  });

  const GenerateCustomToolbar = ({ value, clearSearch, onChange }) => {
    const searchInputRef = useRef(null);
    const [showSearch, setShowSearch] = useState(false);

    useEffect(() => {
      if (showSearch && searchInputRef?.current) {
        searchInputRef.current.focus();
      }
    }, [searchInputRef, showSearch]);

    return (
      <Box className="toolbar-container">
        <Box className="left-toolbar-container">
          {enableSearch && showSearch ? (
            <DebounceInput
              inputRef={searchInputRef}
              debounceTimeout={250}
              variant="outlined"
              value={value}
              placeholder="Search"
              onChange={onChange}
              element={GenerateSearchInput}
              clearSearch={clearSearch}
              hideSearch={() => setShowSearch(false)}
            />
          ) : (
            <Typography variant="h6" style={{ fontSize: '28px', fontWeight: 300 }}>
              {tableName}
            </Typography>
          )}
        </Box>
        <Box className="right-toolbar-container">
          {enableSearch && (
            <Box className="search-container">
              <IconButton
                className="toolbar-search-button"
                id="qa-list-search-button"
                onClick={() => setShowSearch(searchInputRef.current?.value !== '')}
              >
                <SearchIcon />
                <Typography>Search</Typography>
              </IconButton>
            </Box>
          )}
          {actionButton}
        </Box>
      </Box>
    );
  };

  return GenerateCustomToolbar;
};

const escapeRegExp = (value) => value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
export const filterData = (data, searchText, includeFunc) => {
  const searchRegex = new RegExp(escapeRegExp(searchText), 'i');
  return data?.filter((object) => Object.keys(object).some((field) => includeFunc(object, field, searchRegex)));
};

export const UserAdminDataGrid = ({ rowClassFunc = (row) => '', ...props }) => {
  return (
    <DataGridPro
      className="base-datagrid user-admin-datagrid"
      disableSelectionOnClick
      disableColumnReorder
      disableColumnResize
      disableColumnMenu
      disableChildrenSorting
      disableVirtualization
      getRowClassName={({ row, indexRelativeToCurrentPage: index }) =>
        `${rowClassFunc(row)} mui-row-${index % 2 ? 'even' : 'odd'}`
      }
      {...props}
    />
  );
};

export const UserAdminNoRows = () => (
  <Typography className="no-rows-found" id="no-rows-found">
    Sorry, no matching records found
  </Typography>
);
